import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { PageHeader, Badge, Select, Money, Button, Modal, Field, Input, Textarea, Card, EmptyState } from '../components/ui';
import { Can } from '../components/Can';

type B = Record<string, any>;

const MONTHS = 12;
const monthKeys = (start: string) => Array.from({ length: MONTHS }, (_, i) => {
  const d = new Date(`${start}T00:00:00Z`);
  d.setUTCMonth(d.getUTCMonth() + i);
  return d.toISOString().slice(0, 10);
});

/**
 * Budgets and variance.
 *
 * A budget is a grid — account by month — edited as a sheet and stored as rows, which
 * is what turns "how are repairs doing against budget at Elgar House" into a query.
 * A budget scoped to a property is a service-charge budget.
 *
 * Variance is signed so a POSITIVE number is always the good one: under-spending an
 * expense and over-achieving income both read as positive. A report where "+£4,000"
 * against repairs means overspend gets misread in every board meeting.
 */
export default function BudgetsPage() {
  const qc = useQueryClient();
  const [entityId, setEntityId] = useState('');
  const [selected, setSelected] = useState<B | null>(null);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(false);
  const [grid, setGrid] = useState<Record<string, string>>({});
  const [form, setForm] = useState({ name: '', fiscalYearStart: `${new Date().getUTCFullYear()}-04-01`, propertyId: '', notes: '' });

  const entities = useQuery({ queryKey: ['entities'], queryFn: async () => (await api.get<B[]>('/entities')).data });
  const properties = useQuery({ queryKey: ['properties-picker'], queryFn: async () => (await api.get<B[]>('/properties', { perPage: 200 })).data });
  const list = useQuery({ queryKey: ['budgets', entityId], queryFn: async () => (await api.get<B[]>('/budgets', { entityId: entityId || undefined })).data });
  const detail = useQuery({ queryKey: ['budget', selected?.id], queryFn: async () => (await api.get<B>(`/budgets/${selected!.id}`)).data, enabled: !!selected });
  const variance = useQuery({ queryKey: ['budget-variance', selected?.id], queryFn: async () => (await api.get<B>(`/budgets/${selected!.id}/variance`)).data, enabled: !!selected && !editing });
  const target = entityId || entities.data?.[0]?.id || '';
  const accounts = useQuery({ queryKey: ['coa-pl', target], queryFn: async () => (await api.get<B[]>('/accounts', { entityId: target, isPostable: true })).data, enabled: !!target && (creating || editing) });

  const refresh = () => { void qc.invalidateQueries({ queryKey: ['budgets'] }); if (selected) { void qc.invalidateQueries({ queryKey: ['budget', selected.id] }); void qc.invalidateQueries({ queryKey: ['budget-variance', selected.id] }); } };

  const create = useMutation({
    mutationFn: async () => (await api.post<B>('/budgets', { entityId: target, name: form.name, fiscalYearStart: form.fiscalYearStart, propertyId: form.propertyId || undefined, notes: form.notes || undefined })).data,
    onSuccess: (b) => { setCreating(false); refresh(); setSelected(b); },
  });
  const save = useMutation({
    mutationFn: async (status?: string) => {
      const months = monthKeys(detail.data!.fiscalYearStart);
      const lines = Object.entries(grid).flatMap(([key, value]) => {
        const amount = Number(value);
        if (!Number.isFinite(amount) || amount === 0) return [];
        const [accountId, idx] = key.split('|');
        return [{ accountId: accountId!, periodMonth: months[Number(idx)]!, amountMinor: Math.round(amount * 100) }];
      });
      return api.patch(`/budgets/${selected!.id}`, { lines, ...(status ? { status } : {}) });
    },
    onSuccess: () => { setEditing(false); refresh(); },
  });

  const plAccounts = useMemo(() => (accounts.data ?? []).filter((a) => ['INCOME', 'EXPENSE'].includes(a.accountType)), [accounts.data]);
  const months = detail.data ? monthKeys(detail.data.fiscalYearStart) : [];

  const startEditing = () => {
    const next: Record<string, string> = {};
    const idx = new Map(months.map((m, i) => [m, i]));
    for (const l of detail.data?.lines ?? []) {
      const i = idx.get(l.periodMonth);
      if (i !== undefined) next[`${l.accountId}|${i}`] = (l.amountMinor / 100).toFixed(2);
    }
    setGrid(next);
    setEditing(true);
  };

  const v = variance.data;

  return (
    <div>
      <PageHeader
        title="Budgets"
        subtitle="Account by month, for the company or for one property. Variance is signed so a positive number always means it went well."
        actions={<Can do="budgets.create"><div className="flex items-center gap-2">
          <Select value={entityId} onChange={(e) => setEntityId(e.target.value)}><option value="">All companies</option>{entities.data?.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}</Select>
          <Button icon="Plus" onClick={() => setCreating(true)}>New budget</Button>
        </div></Can>}
      />

      {!list.data?.length ? (
        <EmptyState icon="Target" title="No budgets yet" body="Create one for the financial year and fill in the grid; actuals come from the ledger automatically." />
      ) : (
        <div className="mb-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {list.data.map((b) => (
            <button key={b.id} onClick={() => { setSelected(b); setEditing(false); }} className={`surface p-4 text-left transition hover:border-bronze-300 ${selected?.id === b.id ? 'ring-2 ring-bronze-200' : ''}`}>
              <div className="flex items-start justify-between gap-2"><span className="font-medium text-ink-900">{b.name}</span><Badge value={b.status} /></div>
              <div className="mt-1 text-[11.5px] text-ink-400">Year from {b.fiscalYearStart}{b.propertyName ? ` · ${b.propertyName}` : ' · company-wide'}</div>
              <div className="mt-2 text-lg font-semibold tabular-nums"><Money minor={b.totalMinor} currency={b.currency} /></div>
            </button>
          ))}
        </div>
      )}

      {selected && detail.data && (
        <Card
          title={detail.data.name}
          subtitle={editing ? 'Editing the grid — figures are in major units' : 'Budget against actual for the year to date'}
          actions={<div className="flex gap-2">
            {!editing && <Can do="budgets.update"><Button size="sm" variant="secondary" onClick={startEditing}>Edit grid</Button></Can>}
            {!editing && detail.data.status !== 'ACTIVE' && <Can do="budgets.update"><Button size="sm" onClick={() => save.mutate('ACTIVE')} disabled={save.isPending}>Make active</Button></Can>}
            {editing && <><Button size="sm" variant="secondary" onClick={() => setEditing(false)}>Cancel</Button><Button size="sm" onClick={() => save.mutate(undefined)} disabled={save.isPending}>{save.isPending ? 'Saving…' : 'Save grid'}</Button></>}
          </div>}
        >
          {editing ? (
            <div className="overflow-x-auto">
              <table className="w-full min-w-max text-[12.5px]">
                <thead><tr className="border-b border-ink-100 text-left text-[10.5px] uppercase tracking-[0.1em] text-ink-400">
                  <th className="py-2 pr-4">Account</th>{months.map((m) => <th key={m} className="px-1 py-2 text-right">{m.slice(2, 7)}</th>)}
                </tr></thead>
                <tbody>
                  {plAccounts.map((a) => (
                    <tr key={a.id} className="border-b border-ink-50 last:border-0">
                      <td className="py-1 pr-4 whitespace-nowrap text-ink-700">{a.code} {a.name}</td>
                      {months.map((_, i) => (
                        <td key={i} className="px-0.5 py-1">
                          <input
                            className="w-[72px] rounded-lg border border-ink-200 px-1.5 py-1 text-right text-[12px] tabular-nums outline-none focus:border-bronze-400"
                            value={grid[`${a.id}|${i}`] ?? ''}
                            onChange={(e) => setGrid({ ...grid, [`${a.id}|${i}`]: e.target.value })}
                            placeholder="0.00"
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
              {save.isError && <p className="mt-2 text-xs text-red-600">{(save.error as Error).message}</p>}
            </div>
          ) : v ? (
            <>
              <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
                {[
                  ['Income budget', v.totals.incomeBudgetMinor], ['Income actual', v.totals.incomeActualMinor],
                  ['Expense budget', v.totals.expenseBudgetMinor], ['Expense actual', v.totals.expenseActualMinor],
                ].map(([l, val]) => (
                  <div key={String(l)} className="rounded-xl bg-ink-50/70 p-3"><div className="text-[11px] text-ink-500">{l}</div><div className="mt-0.5 font-semibold tabular-nums"><Money minor={val as number} currency={v.currency} /></div></div>
                ))}
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-[13px]">
                  <thead><tr className="border-b border-ink-100 text-left text-[10.5px] uppercase tracking-[0.14em] text-ink-400">
                    <th className="py-2">Account</th><th className="py-2 text-right">Budget</th><th className="py-2 text-right">Actual</th><th className="py-2 text-right">Variance</th><th className="py-2 text-right">%</th>
                  </tr></thead>
                  <tbody>
                    {v.rows.map((r: B) => (
                      <tr key={r.accountId} className="border-b border-ink-50 last:border-0">
                        <td className="py-1.5 text-ink-700">{r.code} {r.name}<span className="ml-1.5 text-[10.5px] text-ink-400">{r.accountType.toLowerCase()}</span></td>
                        <td className="py-1.5 text-right text-ink-500"><Money minor={r.budgetMinor} currency={v.currency} /></td>
                        <td className="py-1.5 text-right"><Money minor={r.actualMinor} currency={v.currency} /></td>
                        <td className={`py-1.5 text-right font-medium ${r.isFavourable ? 'text-emerald-700' : 'text-red-600'}`}><Money minor={r.varianceMinor} currency={v.currency} /></td>
                        <td className={`py-1.5 text-right ${r.isFavourable ? 'text-emerald-700' : 'text-red-600'}`}>{r.variancePct === null ? '—' : `${r.variancePct}%`}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : <p className="text-[13px] text-ink-500">Loading…</p>}
        </Card>
      )}

      {/* Create */}
      <Modal open={creating} onClose={() => setCreating(false)} title="New budget" footer={<>
        <Button variant="secondary" onClick={() => setCreating(false)}>Cancel</Button>
        <Button onClick={() => create.mutate()} disabled={create.isPending || !form.name}>Create</Button>
      </>}>
        <div className="space-y-3">
          <Field label="Name"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Operating budget 2026/27" className="w-full" /></Field>
          <Field label="Financial year starts"><Input type="date" value={form.fiscalYearStart} onChange={(e) => setForm({ ...form, fiscalYearStart: e.target.value })} className="w-full" /></Field>
          <Field label="Property" hint="A budget scoped to a property is a service-charge budget. Leave blank for a company budget.">
            <Select value={form.propertyId} onChange={(e) => setForm({ ...form, propertyId: e.target.value })} className="w-full"><option value="">— company-wide —</option>{properties.data?.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</Select>
          </Field>
          <Field label="Notes"><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full" /></Field>
          {create.isError && <p className="text-xs text-red-600">{(create.error as Error).message}</p>}
        </div>
      </Modal>
    </div>
  );
}
